import {
  CommunicationMethod,
  getAsChoices,
  MembershipStatus,
  UserRole as UserRoleEnum,
} from "../enums";
import {
  BooleanField,
  DateTimeField,
  EmailField,
  NumberField,
  RelationshipField,
  RelationshipFieldMany,
  SelectField,
  StringField,
} from "../plugins/Cruxtify/field-types";
import {
  ArrayStringProperty,
  BooleanProperty,
  DateTimeProperty,
  EmailProperty,
  NumberProperty,
  RelationshipProperty,
  RelationshipPropertyMany,
  StringProperty,
} from "../plugins/Cruxtify/property-types";
import {
  UserPermission,
  UserPermissions,
  UserRole,
} from "../plugins/Cruxtify/UserPermission";
import { ErCrudModel } from "./ErCrudModel";

export class User extends ErCrudModel {
  public static icon = "mdi-account";
  protected static _typeLabel = "User";
  protected static asProperty = "user";
  public static api = {
    path: "users",
  };

  protected userIdField = ["id"];
  protected static routeBase = "users";

  public static modalEdit = true;

  protected labelGlue: string = " ";

  protected static userPermissions = new UserPermissions(
    {
      [UserRole.SuperAdmin]: UserPermission.Edit,
      [UserRole.EntityOwner]: UserPermission.Edit,
      [UserRole.LoggedIn]: UserPermission.Read,
    },
    UserPermission.New
  );

  protected static propertyDefinitions = [
    {
      type: NumberProperty,
      opts: {
        name: "id",
        label: "ID",
        userPermissions: UserPermission.Hidden,
      },
    },
    {
      type: NumberProperty,
      opts: {
        name: "legacy_id",
        label: "ID",
        userPermissions: UserPermission.Hidden,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "created_at",
        label: "Created At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "updated_at",
        label: "Updated At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "first_name",
        label: "First Name",
        sortable: true,
        rules: ["required"],
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "last_name",
        label: "Last Name",
        sortable: true,
        rules: ["required"],
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "phone",
        label: "Phone Number",
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "mobile_phone",
        label: "Mobile Phone Number",
      },
    },
    {
      type: ArrayStringProperty,
      opts: {
        name: "preferred_communication_methods",
        label: "Preferred Communication Method(s)",
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "how_did_you_hear",
        label: "How did you hear about us?",
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "law_firm_role",
        label: "Law Firm Role",
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "party",
        label: "Party",
      },
    },
    {
      type: ArrayStringProperty,
      opts: {
        name: "practice_areas",
        label: "Practice Area(s)",
      },
    },
    {
      type: EmailProperty,
      opts: {
        name: "email",
        label: "Email",
        sortable: true,
        rules: ["required"],
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "invoice_cc_emails",
        label: "Invoice CC Emails",
        // Billing config, not something the attorney maintains about themselves
        // — same visibility as the other admin-only account fields below.
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          UserPermission.Hidden
        ),
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "password",
        label: "Password",
        sortable: false,
      },
    },
    {
      type: BooleanProperty,
      opts: {
        name: "is_admin",
        computed: true,
      },
    },
    {
      type: BooleanProperty,
      opts: {
        name: "is_verified",
        label: "User Email Verified",
        // Writable, but this permission is cosmetic — Cruxtify's backend has
        // no per-property permissions, so
        // UserRequestHandler::rejectUnauthorizedEmailVerification is the
        // actual gate. A reader who trusts this line alone is one `curl`
        // away from being wrong.
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          UserPermission.Hidden
        ),
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "membership_status",
        label: "Membership",
        // Read-only: the API derives it from the account's Stripe
        // subscription (App\Enums\MembershipStatus), so there is nothing here
        // to write back.
        computed: true,
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Read,
          },
          UserPermission.Hidden
        ),
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "user_role",
        label: "Account Type",
        rules: ["required"],
        // Default new accounts to Client. Without a default the SelectField
        // renders blank with "Admin" sitting at the top of the list (the
        // UserRole enum's first member), making it easy to save a user — most
        // damagingly a managed attorney — as an admin. Managed/attorney accounts
        // are the common admin-created case; experts/admins are the exception
        // and still selectable. The backend enforces the managed⇒client
        // invariant regardless (UserRequestHandler::enforceManagedUserIsClient).
        default: UserRoleEnum.Client,
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          UserPermission.Hidden
        ),
      },
    },
    {
      type: BooleanProperty,
      opts: {
        name: "is_managed",
        label: "Managed User",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          UserPermission.Hidden
        ),
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        // snake_case to match the backend relationship name / serialized API
        // key (Eloquent snake_cases relationship keys); see User model
        // `case_manager` BelongsTo in the API repo.
        name: "case_manager",
        label: "Case Manager",
        relatedModel: "User",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          UserPermission.Hidden
        ),
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "stripe_id",
        label: "Stripe ID",
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "pm_type",
        label: "Payment Method Type",
        userPermissions: UserPermission.Hidden,
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "pm_last_four",
        label: "Payment Method Last Four",
        userPermissions: UserPermission.Hidden,
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "trial_ends_at",
        label: "Trial Ends At",
        userPermissions: UserPermission.Hidden,
      },
    },

    {
      type: RelationshipPropertyMany,
      opts: {
        name: "experts",
        label: "Expert Account",
        relatedModel: "Expert",
        foreignProperty: "user",
      },
    },
    {
      type: RelationshipPropertyMany,
      opts: {
        name: "credits",
        label: "Credits",
        relatedModel: "Credit",
        foreignProperty: "user",
      },
    },
    {
      type: RelationshipPropertyMany,
      opts: {
        name: "subscriptions",
        label: "Subscriptions",
        relatedModel: "Subscription",
        foreignProperty: "user",
      },
    },
  ];
  protected static fieldDefinitions = [
    { type: NumberField, opts: { property: "id" } },
    { type: NumberField, opts: { property: "legacy_id" } },
    { type: DateTimeField, opts: { property: "created_at" } },
    { type: DateTimeField, opts: { property: "updated_at" } },
    {
      type: SelectField,
      opts: { property: "user_role", options: getAsChoices(UserRoleEnum) },
    },
    {
      type: SelectField,
      opts: {
        property: "membership_status",
        options: getAsChoices(MembershipStatus),
      },
    },
    // ---------------------------------------------------------------------
    // Half-width fields are `cols: 12, md: 6`, NOT a bare `cols: 6`.
    //
    // `FieldColumns.vue:3-9` passes cols/sm/md/lg/xl straight through to
    // `v-col`, where `cols` is the *xs* breakpoint — so a bare `cols: 6` stays
    // half-width all the way down to a phone and never stacks. Every field
    // below used to be a bare `cols: 6`, which is why the registration form did
    // not collapse on a phone at all. (Plan 0010, Task 9.)
    // ---------------------------------------------------------------------
    {
      type: StringField,
      opts: { property: "first_name", isPrimaryLabelField: 1, cols: 12, md: 6 },
    },
    {
      type: StringField,
      opts: { property: "last_name", isPrimaryLabelField: 2, cols: 12, md: 6 },
    },
    {
      type: StringField,
      opts: { property: "phone", cols: 12, md: 6 },
    },
    {
      type: StringField,
      opts: { property: "mobile_phone", cols: 12, md: 6 },
    },
    // ---------------------------------------------------------------------
    // These two are a DIFFERENT change from the six above, with a wider blast
    // radius, so it is recorded rather than folded into the comment above.
    //
    // They were already `cols: 12` and already stacked correctly; they are
    // halved purely to match `signup_step2_contact.html`'s grid. Field width
    // is a property of the field definition and `CrudFieldQuery` has no
    // per-layout override, so this also halves them in the `my-account` layout
    // (pinned to `maxWidth: "600px"`, so they land at roughly 290px each — one
    // of them a multi-select chip field) and in the admin `Crudform`. Confirm
    // both visually; revert these two if 290px is too tight for the chips.
    // ---------------------------------------------------------------------
    {
      // `multiple: true` is deliberate and stays. `signup_step2_contact.html`
      // draws a single-select here, but that is a mockup simplification —
      // narrowing it would change what the app does, which the plan's golden
      // rule forbids. Only the width is halved.
      type: SelectField,
      opts: {
        property: "preferred_communication_methods",
        multiple: true,
        cols: 12,
        md: 6,
        options: getAsChoices(CommunicationMethod),
      },
    },
    {
      type: StringField,
      opts: { property: "how_did_you_hear", cols: 12, md: 6 },
    },
    {
      type: SelectField,
      opts: {
        property: "law_firm_role",
        cols: 12,
        md: 6,
        options: {
          owner_partner: "Owner/Partner",
          attorney: "Attorney",
          other: "Other",
        },
        conditional: {
          property: "user_role",
          value: UserRoleEnum.Client,
        },
      },
    },
    {
      type: SelectField,
      opts: {
        property: "party",
        cols: 12,
        md: 6,
        options: {
          plaintiff: "Plaintiff",
          defendant: "Defense",
        },
        conditional: {
          property: "user_role",
          value: UserRoleEnum.Client,
        },
      },
    },
    {
      type: SelectField,
      opts: {
        property: "practice_areas",
        multiple: true,
        cols: 12,
        options: {
          med_mal: "Medical Malpractice",
          personal_injury: "Personal Injury",
          other: "Other",
        },
        conditional: {
          property: "user_role",
          value: UserRoleEnum.Client,
        },
      },
    },
    { type: StringField, opts: { property: "stripe_id" } },
    {
      // The mockup's leading mail icon sits INSIDE the box, so it is a
      // `prepend-inner-icon`, delivered through `formComponentProps` (which
      // `Field.vue` spreads into the `inputProps` it binds onto the
      // `v-text-field`). This is the repo's first use of that door.
      //
      // This half does NOT stand alone. `EmailField` declares a class-level
      // `icon` of `mdi-email`, which Cruxtify renders as a `prepend-icon`
      // OUTSIDE the box, and `CrudField`'s `icon` getter falls back to the
      // class value on any falsy instance value — so `icon: ""` here reads as
      // "unset" and cannot clear it. The rule that hides that outer envelope
      // lives in `assets/screens/_signup.scss` §8, which carries the full
      // argument and the recommended deeper fix. Neither half is correct alone.
      type: EmailField,
      opts: {
        property: "email",
        cols: 12,
        rules: ["email"],
        formComponentProps: { "prepend-inner-icon": "mdi-email-outline" },
      },
    },
    {
      type: StringField,
      opts: {
        property: "invoice_cc_emails",
        cols: 12,
        description:
          "Comma-separated. Prefilled as the CC list every time an invoice is sent to this attorney.",
      },
    },
    { type: BooleanField, opts: { property: "is_verified" } },
    { type: BooleanField, opts: { property: "is_managed", cols: 6 } },
    {
      type: RelationshipField,
      opts: {
        property: "case_manager",
        cols: 6,
        rules: ["required"],
        conditional: { property: "is_managed" },
      },
    },
    { type: StringField, opts: { property: "password" } },
    {
      type: RelationshipFieldMany,
      opts: {
        property: "experts",
        viewComponentName: "form-relationship-field-many-table",
        formComponentName: "form-relationship-field-many-table",
        collectionLayoutOpts: {
          useDynamicFields: false,
          enableSearch: false,
          noRefresh: true,
          noNew: true,
          enableDynamicFilters: false,
        },
        // conditional: {
        //   property: "user_role",
        //   value: UserRoleEnum.Expert,
        // },
      },
    },
    {
      type: RelationshipFieldMany,
      opts: {
        property: "credits",
        // "Credits", not "Purchase History": credit packs are no longer sold,
        // so the only rows this table can hold now are credits issued against
        // a case rather than bought. Only the admin `Crudform` layout renders
        // it -- `my-account` has no fieldset for it -- so this copy is
        // admin-facing (asana-1388).
        label: "Credits",
        viewComponentName: "form-relationship-field-many-table",
        formComponentName: "form-relationship-field-many-table",
        collectionLayoutOpts: {
          enableSearch: false,
          noRefresh: true,
          enableDynamicFilters: false,
          disableRowClick: true,
          // Without this the empty state renders as a blank slab under the
          // heading, which reads as a failed load rather than an empty one.
          // Flagged from `_billing.scss` at Task 13, which could not set it —
          // the field lives here, on another task's model. The copy no longer
          // invites buying a credit: credit packs are retired, so pointing an
          // admin at a purchase flow that does not exist is what got this
          // reported (asana-1388).
          noneText: "This account holds no credits.",
        },
        // conditional: {
        //   property: "user_role",
        //   value: UserRoleEnum.Client,
        // },
      },
    },
    {
      type: RelationshipFieldMany,
      opts: {
        property: "subscriptions",
        viewComponentName: "form-relationship-field-many-table",
        formComponentName: "form-relationship-field-many-table",
        collectionLayoutOpts: {
          useDynamicFields: false,
          enableSearch: false,
          noRefresh: true,
          enableDynamicFilters: false,
          // Same reason as `credits` above, and now reachable: until the API
          // learned to apply this table's `?user=` filter (asana-1388) every
          // user looked subscribed, so the empty state never rendered. With
          // the filter honoured, a user with no plan hits it — and with no
          // noneText that is a blank slab under the heading, which is the
          // "something is broken here" shape this card was reported for.
          noneText: "This account has no subscription.",
        },
        // conditional: {
        //   property: "user_role",
        //   value: UserRoleEnum.Client,
        // },
      },
    },
    {
      type: NumberField,
      opts: {
        id: "switch-user",
        property: "id",
        label: "Impersonate",
        viewComponentName: "switch-user",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Read,
          },
          UserPermission.Hidden
        ),
      },
    },
  ];

  protected static modelLayoutDefinitions = [
    {
      id: "Crudform",
      fieldSets: [
        {
          id: "0",
          fields: [
            "first_name",
            "last_name",
            "email",
            "phone",
            "mobile_phone",
            "preferred_communication_methods",
            "how_did_you_hear",
            "is_verified",
            "user_role",
            "is_managed",
            "case_manager",
            "law_firm_role",
            "party",
            "practice_areas",
          ],
        },
        {
          id: "1",
          fields: ["experts", "credits", "subscriptions"],
        },
        {
          id: "meta",
          fields: ["created_at", "updated_at", "id", "stripe_id"],
        },
      ],
    },
    {
      id: "Registration",
      fieldSets: [
        {
          id: "0",
          fields: ["first_name", "last_name", "email"],
        },
      ],
    },
    {
      id: "RegistrationClient",
      fieldSets: [
        {
          id: "0",
          fields: [
            "first_name",
            "last_name",
            "email",
            "phone",
            "mobile_phone",
            "preferred_communication_methods",
            "how_did_you_hear",
            "law_firm_role",
            "party",
            "practice_areas",
          ],
        },
      ],
    },
    {
      id: "RegistrationExpert",
      fieldSets: [
        {
          id: "0",
          fields: [
            "first_name",
            "last_name",
            "email",
            "phone",
            "mobile_phone",
            "preferred_communication_methods",
            "how_did_you_hear",
          ],
        },
      ],
    },
    {
      id: "my-account",
      maxWidth: "600px",
      hideDelete: true,
      fieldSets: [
        {
          id: "0",
          fields: [
            "first_name",
            "last_name",
            "email",
            "phone",
            "mobile_phone",
            "preferred_communication_methods",
            "how_did_you_hear",
            "law_firm_role",
            "party",
            "practice_areas",
          ],
        },
      ],
    },
  ];

  protected static collectionLayoutDefinitions = [
    {
      id: "table",
      columnFields: [
        "created_at",
        "first_name",
        "last_name",
        "email",
        "user_role",
        "membership_status",
        "is_verified",
        "is_managed",
        "switch-user",
      ],
      useModal: true,
      enableSearch: true,
      enableDynamicFilters: true,
      defaultSort: [
        {
          field: "created_at",
          order: "desc",
        },
      ],
    },
  ];

  public async registerUser() {
    return this.$nuxt.$api
      .POST("register", this.toApiPayload(), "")
      .then((res) => res.data);
  }

  /**
   * asana-1502: does the register wizard's contact step gate on -- an email
   * belonging to a managed user gets the account-claim panel instead of
   * walking all the way to the "already taken" dead end at Create account.
   */
  public static accountClaimStatus(
    email: string
  ): Promise<{ claimable: boolean }> {
    return this.$nuxt.$api.POST("account-claim-status", { email });
  }

  public switchUser() {
    return this.$nuxt.$api.POST("switch-user/" + this.id);
  }

  public static endSwitchUser() {
    return this.$nuxt.$api.POST("end-switch-user");
  }
}
